/**
 * Orthogonal visibility graph (Hanan grid) over a set of rectangle
 * obstacles plus a set of port points.
 *
 * Insight (Hanan, 1966): for orthogonal paths between endpoints A
 * and B over a set of rectangular obstacles, the OPTIMAL path only
 * ever turns at x-coordinates drawn from the set of all rectangle
 * edges (plus A.x and B.x) and y-coordinates drawn from the set of
 * all rectangle edges (plus A.y and B.y). So the search space
 * reduces from continuous 2D to an O(n²) grid.
 *
 * We also insert x/y coordinates offset by `clearance` from each
 * obstacle so edges can run along the "breathing room" around each
 * box instead of hugging its border.
 *
 * This file just constructs the grid and reports which cells are
 * occupied; the pathfinder in `OrthogonalRouter.ts` does the
 * shortest-path search on top of it.
 */

import type { Rect, Point, Obstacle } from './types';

export interface HananGrid {
  /** Sorted unique x-coordinates. */
  xs: number[];
  /** Sorted unique y-coordinates. */
  ys: number[];
  /** `blocked[i * ys.length + j] === true` means grid cell
   *  (xs[i], ys[j]) falls inside the strict interior of some
   *  blocking obstacle and is unusable as a vertex. */
  blockedVertex: Uint8Array;
  /** Obstacle rects copied locally for the segment-clearance tests. */
  obstacles: ReadonlyArray<Rect>;
  /** Minimum visual gap to keep around non-endpoint obstacles. */
  visualClearance: number;
  /** Snap an arbitrary x-coordinate to the nearest grid column.
   *  Returns -1 if not on the grid. */
  xIndex(x: number): number;
  yIndex(y: number): number;
}

interface BuildInput {
  obstacles: Obstacle[];
  /** Extra x/y coordinates to add to the grid — typically port
   *  points + obstacle-offset lines. */
  extraPoints: Point[];
  clearance: number;
  visualClearance?: number;
}

export function buildHananGrid(input: BuildInput): HananGrid {
  const { obstacles, extraPoints, clearance } = input;
  const visualClearance = input.visualClearance ?? clearance;

  // ── Collect candidate x/y coordinates ──────────────────────
  // Each rect contributes four coordinate lines, each at ±clearance
  // from the border so paths can run through the clearance channel.
  const xSet = new Set<number>();
  const ySet = new Set<number>();
  const addX = (v: number): void => { xSet.add(Math.round(v)); };
  const addY = (v: number): void => { ySet.add(Math.round(v)); };

  const clearanceOffsets = Array.from(new Set([
    Math.max(0, clearance),
    Math.max(0, visualClearance),
  ]));

  for (const o of obstacles) {
    const { rect } = o;
    for (const offset of clearanceOffsets) {
      addX(rect.x - offset);
      addX(rect.x + rect.width + offset);
      addY(rect.y - offset);
      addY(rect.y + rect.height + offset);
    }
    addX(rect.x);
    addX(rect.x + rect.width);
    addY(rect.y);
    addY(rect.y + rect.height);
  }
  for (const p of extraPoints) {
    addX(p.x);
    addY(p.y);
  }

  const xs = [...xSet].sort((a, b) => a - b);
  const ys = [...ySet].sort((a, b) => a - b);

  const xIndexMap = new Map<number, number>();
  xs.forEach((v, i) => xIndexMap.set(v, i));
  const yIndexMap = new Map<number, number>();
  ys.forEach((v, i) => yIndexMap.set(v, i));

  // ── Mark blocked vertices ──────────────────────────────────
  // A vertex at (xs[i], ys[j]) is blocked if it lies in the STRICT
  // interior of any blocking obstacle — EXCEPT those flagged
  // `segmentOnly`. Segment-only obstacles (container frames) block
  // segments that try to cross their border but leave grid
  // vertices inside them usable, so a leaf port sitting inside
  // a frame can still be the start/end of a route.
  const blockedVertex = new Uint8Array(xs.length * ys.length);
  const vertexBlockers = obstacles
    .filter((o) => o.blocking !== false && !o.segmentOnly)
    .map((o) => o.rect);
  for (let i = 0; i < xs.length; i++) {
    for (let j = 0; j < ys.length; j++) {
      const x = xs[i];
      const y = ys[j];
      for (const r of vertexBlockers) {
        const blocked = inflateRect(r, visualClearance);
        if (
          x > blocked.x &&
          x < blocked.x + blocked.width &&
          y > blocked.y &&
          y < blocked.y + blocked.height
        ) {
          blockedVertex[i * ys.length + j] = 1;
          break;
        }
      }
    }
  }

  // Segment-level blockers are the wider set: includes segment-only
  // obstacles (frames) so A* has to route around them unless the
  // edge adds the frame to its per-route ignoreRects.
  const segmentBlockers = obstacles
    .filter((o) => o.blocking !== false)
    .map((o) => o.rect);
  const obsCopy = segmentBlockers.slice();

  return {
    xs,
    ys,
    blockedVertex,
    obstacles: obsCopy,
    visualClearance,
    xIndex(x) {
      return xIndexMap.get(Math.round(x)) ?? -1;
    },
    yIndex(y) {
      return yIndexMap.get(Math.round(y)) ?? -1;
    },
  };
}

/**
 * True when the axis-aligned segment between (x1,y1) and (x2,y2)
 * (exactly one of dx/dy must be zero) passes through the strict
 * interior of any blocking obstacle in the grid.
 *
 * Pure geometry — used by the A* router to decide whether a grid
 * edge is traversable. Exposed here so the router can call it without
 * re-implementing clearance semantics.
 */
export function segmentCrossesObstacle(
  grid: HananGrid,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  /** Obstacle rects to ignore — typically the source and target
   *  rects so the first/last segment can enter/exit them. */
  ignore: ReadonlyArray<Rect> = [],
): boolean {
  const isHorizontal = y1 === y2;
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);

  for (const r of grid.obstacles) {
    if (ignore.includes(r)) continue;
    const blocked = inflateRect(r, grid.visualClearance);
    if (isHorizontal) {
      const y = y1;
      // Segment at y horizontally from minX..maxX
      if (y <= blocked.y || y >= blocked.y + blocked.height) continue; // outside strict y-interior
      if (maxX <= blocked.x || minX >= blocked.x + blocked.width) continue; // x-range doesn't overlap
      return true;
    } else {
      const x = x1;
      if (x <= blocked.x || x >= blocked.x + blocked.width) continue;
      if (maxY <= blocked.y || minY >= blocked.y + blocked.height) continue;
      return true;
    }
  }
  return false;
}

function inflateRect(r: Rect, amount: number): Rect {
  if (amount <= 0) return r;
  return {
    x: r.x - amount,
    y: r.y - amount,
    width: r.width + amount * 2,
    height: r.height + amount * 2,
  };
}
